import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search, SlidersHorizontal, Smile } from 'lucide-react'
import { cn } from '../lib/utils'
import PitchSheetB from './room/PitchSheetB'
import EmojiSheetB from './room/EmojiSheetB'
import useSheetDragDismiss from '../hooks/useSheetDragDismiss'

const PANEL_ANIMATION_MS = 300
const PANEL_OPEN_DELAY_MS = 10

const BottomActionBar = ({
  onSearchClick,
  pitchShift = 0,
  onPitchShiftChange,
  getKeyDisplay,
  onSendEmoji
}) => {
  const [activePanel, setActivePanel] = useState(null) // 'pitch' | 'emoji' | null
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)

  const isPanelOpen = activePanel !== null
  const searchLabel = useMemo(() => 'Search songs or paste YouTube URL', [])

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const closePanel = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    setIsPanelVisible(false)
    closeTimerRef.current = window.setTimeout(() => {
      setActivePanel(null)
      closeTimerRef.current = null
    }, PANEL_ANIMATION_MS)
  }, [clearCloseTimer, clearOpenTimer])

  const openPanel = useCallback((panel) => {
    clearCloseTimer()

    if (activePanel) {
      setActivePanel(panel)
      setIsPanelVisible(true)
      return
    }

    setActivePanel(panel)
    setIsPanelVisible(false)
    clearOpenTimer()
    openTimerRef.current = window.setTimeout(() => {
      setIsPanelVisible(true)
      openTimerRef.current = null
    }, PANEL_OPEN_DELAY_MS)
  }, [activePanel, clearCloseTimer, clearOpenTimer])

  const handleTogglePanel = useCallback((panel) => {
    if (activePanel === panel) {
      closePanel()
      return
    }
    openPanel(panel)
  }, [activePanel, closePanel, openPanel])

  const {
    dragHandleProps,
    dragOffset,
    isDragging,
    resetDrag
  } = useSheetDragDismiss({
    enabled: isPanelOpen && isPanelVisible,
    onDismiss: closePanel
  })

  useEffect(() => {
    if (!isPanelVisible) {
      resetDrag()
    }
  }, [isPanelVisible, resetDrag])

  useEffect(() => () => {
    clearOpenTimer()
    clearCloseTimer()
  }, [clearCloseTimer, clearOpenTimer])

  return (
    <>
      {isPanelOpen ? (
        <div className="fixed inset-0 z-[65]">
          <button
            type="button"
            aria-label="Close utility sheet"
            onClick={closePanel}
            className={cn(
              'h-full w-full bg-black/60 backdrop-blur-sm transition-opacity duration-300',
              isPanelVisible ? 'opacity-100' : 'opacity-0'
            )}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0">
            <div
              className="pointer-events-auto room-safe-bottom transition-transform duration-300 ease-out"
              style={{
                transform: isPanelVisible ? `translateY(${dragOffset}px)` : 'translateY(100%)',
                transitionDuration: isDragging ? '0ms' : '300ms'
              }}
            >
              {activePanel === 'pitch' ? (
                <PitchSheetB
                  pitchShift={pitchShift}
                  onPitchShiftChange={onPitchShiftChange}
                  getKeyDisplay={getKeyDisplay}
                  onClose={closePanel}
                  dragHandleProps={dragHandleProps}
                />
              ) : null}
              {activePanel === 'emoji' ? (
                <EmojiSheetB
                  onSendEmoji={onSendEmoji}
                  onClose={closePanel}
                  dragHandleProps={dragHandleProps}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="fixed bottom-0 left-0 right-0 z-[60] border-t border-white/10 bg-[color:rgba(11,19,32,0.92)] backdrop-blur-xl">
        <div className="room-safe-bottom room-safe-inline mx-auto w-full max-w-lg pb-3 pt-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={onSearchClick}
              aria-label="Open search"
              data-tutorial-id="search-launcher"
              className="room-focus-ring room-input flex h-12 min-w-0 flex-1 items-center gap-2 rounded-2xl px-3 text-left text-sm font-medium shadow-[0_10px_24px_rgba(2,8,18,0.3)]"
            >
              <Search className="h-4 w-4 shrink-0 text-[var(--room-muted)]" />
              <span className="min-w-0 truncate text-[var(--room-muted)]">{searchLabel}</span>
            </button>

            <div className="flex shrink-0 items-center gap-2" data-tutorial-id="utility-controls">
              <button
                type="button"
                onClick={() => handleTogglePanel('pitch')}
                className={cn(
                  'room-focus-ring flex h-12 min-w-[64px] items-center justify-center gap-1 rounded-xl border px-2 text-xs font-semibold uppercase tracking-[0.02em] transition-colors',
                  activePanel === 'pitch'
                    ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                    : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                )}
                aria-label="Open pitch controls"
              >
                <SlidersHorizontal className="h-4 w-4 shrink-0" />
                Pitch
              </button>

              <button
                type="button"
                onClick={() => handleTogglePanel('emoji')}
                className={cn(
                  'room-focus-ring flex h-12 min-w-[64px] items-center justify-center gap-1 rounded-xl border px-2 text-xs font-semibold uppercase tracking-[0.02em] transition-colors',
                  activePanel === 'emoji'
                    ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                    : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                )}
                aria-label="Open emoji reactions"
              >
                <Smile className="h-4 w-4 shrink-0" />
                Emoji
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default BottomActionBar
